import { useContext, useEffect } from "react";
import { ListContext, required, useTranslate } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";

import { DateInput } from "@/components/admin/date-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import {
  reasonsFor,
  stagesOf,
  useLostReasons,
  usePipelines,
  usePipelineStages,
} from "./dealData";
import { isOpenStage } from "./nextAction";

// Pipeline and stage of a deal from pipeline_stages (task 1.7), in place of
// Atomic's single stage list. On a lost stage the form asks the reason and,
// when the reason wants one, the note: the database checks both (D-036).
// On an open stage the next action date is required (task 1.8, D-037).
export const TenaxDealStageInputs = () => {
  const translate = useTranslate();
  const list = useContext(ListContext);
  const { setValue } = useFormContext();
  const { data: pipelines = [] } = usePipelines();
  const { data: allStages = [] } = usePipelineStages();
  const { data: reasons = [] } = useLostReasons();
  const pipelineId: number | undefined = useWatch({ name: "pipeline_id" });
  const stageId: number | undefined = useWatch({ name: "stage_id" });
  const reasonId: number | undefined = useWatch({ name: "lost_reason_id" });

  const defaultPipeline: number | undefined =
    list?.filterValues?.pipeline_id ?? pipelines[0]?.id;
  const currentPipeline = pipelineId ?? defaultPipeline;
  const stages = stagesOf(allStages, currentPipeline);
  const stage = stages.find((item) => item.id === stageId);
  const reason = reasons.find((item) => item.id === reasonId);

  // a new deal starts in the pipeline shown on the board, at its first
  // stage; a stage of another pipeline is replaced by the first of this one
  useEffect(() => {
    if (currentPipeline == null || stages.length === 0) return;
    if (pipelineId == null) {
      setValue("pipeline_id", currentPipeline, { shouldDirty: true });
    }
    if (!stages.some((item) => item.id === stageId)) {
      setValue("stage_id", stages[0].id, { shouldDirty: true });
    }
  }, [currentPipeline, pipelineId, stageId, stages, setValue]);

  return (
    <>
      {pipelines.length ? (
        <SelectInput
          source="pipeline_id"
          label="resources.deals.fields.pipeline_id"
          choices={pipelines}
          optionText="name"
          optionValue="id"
          helperText={false}
          validate={required()}
        />
      ) : null}
      <SelectInput
        source="stage_id"
        label="resources.deals.fields.stage"
        choices={stages}
        optionText="name"
        optionValue="id"
        helperText={false}
        validate={required()}
      />
      {isOpenStage(stage) ? (
        <DateInput
          source="next_action_at"
          label="resources.deals.fields.next_action_at"
          helperText={false}
          validate={required()}
        />
      ) : null}
      {stage?.is_lost ? (
        <>
          <SelectInput
            source="lost_reason_id"
            label="resources.deals.fields.lost_reason_id"
            choices={reasonsFor(reasons, currentPipeline)}
            optionText="name"
            optionValue="id"
            helperText={false}
            validate={required()}
          />
          <TextInput
            source="lost_reason"
            label={translate(
              reason?.requires_note
                ? "tenax.deals.lost.note_required"
                : "tenax.deals.lost.note",
            )}
            multiline
            helperText={false}
            validate={reason?.requires_note ? required() : undefined}
          />
        </>
      ) : null}
    </>
  );
};
